// Drawer displaying Knowledge Quality intelligence for a document.
import {
  Star,
  AlertTriangle,
  Loader2,
} from "lucide-react";
import { useMemo, useState } from "react";

import { DataTable } from "../../../components/DataTable";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from "../../../components/ui/sheet";
import {
  DOC_TYPE_COLORS,
  DOC_TYPE_FALLBACK_BADGE_CLS,
  LAYER_ICON_BOX_TONE,
} from "../constants";
import { SectionCard, StatTile } from "../Domain";
import { formatDocType } from "../utils";
import {
  getKQEntityColumns,
  getKQEvidenceColumns,
  getKQFindingColumns,
} from "./knowledgeQualityColumns";
import { PrimaryQualityHighlight } from "./knowledgeQualityIntelligenceParts";
import {
  KQ_DRAWER_WIDTH_CLS,
  KQ_HEADER_COPY,
  KQ_SUMMARY_LABELS,
  KQ_TABLE_MAX_HEIGHT,
  KQ_TABLE_PAGE_SIZE,
} from "./utils/constants";
import {
  deriveKnowledgeIntel,
  pluralize,
  type KQDerived,
} from "./utils/helpers";

import type { KnowledgeQualityResult } from "./knowledgeQualityTypes";

export interface KnowledgeQualityDrawerProps {
  open: boolean;
  onClose: () => void;
  result?: KnowledgeQualityResult | null;
  documentTitle?: string;
  documentType?: string;
  onViewSourceEvidence?: () => void;
}

export function KnowledgeQualityDrawer({
  open,
  onClose,
  result,
  documentTitle,
  documentType,
  onViewSourceEvidence,
}: KnowledgeQualityDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        overlayClassName="z-[80]"
        className={`z-[80] flex h-full ${KQ_DRAWER_WIDTH_CLS} flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-[95vw]`}
      >
        <SheetTitle className="sr-only">{KQ_HEADER_COPY.title}</SheetTitle>
        <SheetDescription className="sr-only">
          {KQ_HEADER_COPY.description}
        </SheetDescription>
        {!result ? (
          <LoadingState />
        ) : (
          <KnowledgeQualityContent
            data={result}
            documentTitle={documentTitle}
            documentType={documentType}
            onViewSourceEvidence={onViewSourceEvidence}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function LoadingState() {
  return (
    <div className="flex flex-1 items-center justify-center gap-2 text-muted-foreground">
      <Loader2 className="size-5 animate-spin" />
      <span className="text-sm">{KQ_HEADER_COPY.loading}</span>
    </div>
  );
}

function useTablePages(docId: string) {
  const [state, setState] = useState({
    key: docId,
    findings: 0,
    entities: 0,
    evidence: 0,
  });
  if (state.key !== docId) {
    setState({ key: docId, findings: 0, entities: 0, evidence: 0 });
  }
  const current = state.key === docId ? state : { ...state, findings: 0, entities: 0, evidence: 0 };
  return {
    findings: current.findings,
    entities: current.entities,
    evidence: current.evidence,
    setFindings: (p: number) => setState((s) => ({ ...s, key: docId, findings: p })),
    setEntities: (p: number) => setState((s) => ({ ...s, key: docId, entities: p })),
    setEvidence: (p: number) => setState((s) => ({ ...s, key: docId, evidence: p })),
  };
}

function KnowledgeQualityContent({
  data,
  documentTitle,
  documentType,
  onViewSourceEvidence,
}: {
  data: KnowledgeQualityResult;
  documentTitle?: string;
  documentType?: string;
  onViewSourceEvidence?: () => void;
}) {
  const derived = useMemo(() => deriveKnowledgeIntel(data), [data]);
  const pages = useTablePages(derived.docId);
  const findingColumns = useMemo(() => getKQFindingColumns(), []);
  const entityColumns = useMemo(() => getKQEntityColumns(), []);
  const evidenceColumns = useMemo(() => getKQEvidenceColumns(), []);

  return (
    <>
      <DrawerHeader
        data={data}
        derived={derived}
        documentTitle={documentTitle}
        documentType={documentType}
      />

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-4">
        {derived.hasMissingAttrsBanner && (
          <MissingAttrsBanner derived={derived} />
        )}

        <SectionCard
          headerLabel={KQ_SUMMARY_LABELS.section}
          rightLabel={KQ_SUMMARY_LABELS.sectionRight}
        >
          <SummaryTiles derived={derived} />
        </SectionCard>

        <SectionCard
          headerLabel={KQ_SUMMARY_LABELS.primary}
          rightLabel={KQ_SUMMARY_LABELS.primaryRight}
        >
          <PrimaryQualityHighlight
            overallPct={derived.overallPct}
            strongCount={derived.strongCount}
            gapsCount={derived.gapsCount}
            richnessPct={derived.richnessPct}
            trustPct={derived.trustPct}
            entitiesCount={derived.summary.knowledge_entities_detected ?? 0}
            richEntities={derived.summary.rich_entities ?? 0}
            evidenceRecords={derived.summary.evidence_records ?? 0}
            duplicateEntities={derived.summary.duplicate_entities ?? 0}
          />
        </SectionCard>

        <SectionCard
          headerLabel={KQ_SUMMARY_LABELS.findings}
          rightLabel={`${derived.qualityFindings.length} ${KQ_SUMMARY_LABELS.findingsRightSuffix}`}
        >
          <div className="p-3">
            <DataTable
              columns={findingColumns}
              data={derived.sortedFindings}
              compact
              maxHeight={KQ_TABLE_MAX_HEIGHT.findings}
              page={pages.findings}
              pageSize={KQ_TABLE_PAGE_SIZE}
              totalItems={derived.qualityFindings.length}
              onPageChange={pages.setFindings}
              itemLabel="findings"
              pagerStyle="simple"
              rowKey={(row, i) =>
                `${row.finding_type ?? "finding"}-${row.affected_entity ?? i}`
              }
            />
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={KQ_SUMMARY_LABELS.entities}
          rightLabel={`${derived.entitiesMerged.length} ${KQ_SUMMARY_LABELS.entitiesRightSuffix}`}
        >
          <div className="p-3">
            <DataTable
              columns={entityColumns}
              data={derived.entitiesMerged}
              compact
              maxHeight={KQ_TABLE_MAX_HEIGHT.entities}
              page={pages.entities}
              pageSize={KQ_TABLE_PAGE_SIZE}
              totalItems={derived.entitiesMerged.length}
              onPageChange={pages.setEntities}
              itemLabel="entities"
              pagerStyle="simple"
              rowKey={(row, i) => row.entity ?? `entity-${i}`}
            />
          </div>
        </SectionCard>

        <SectionCard
          headerLabel={KQ_SUMMARY_LABELS.evidence}
          rightLabel={`${derived.evidenceTrust.length} ${KQ_SUMMARY_LABELS.evidenceRightSuffix}`}
        >
          <div className="p-3">
            <DataTable
              columns={evidenceColumns}
              data={derived.evidenceTrust}
              compact
              maxHeight={KQ_TABLE_MAX_HEIGHT.evidence}
              page={pages.evidence}
              pageSize={KQ_TABLE_PAGE_SIZE}
              totalItems={derived.evidenceTrust.length}
              onPageChange={pages.setEvidence}
              itemLabel="records"
              pagerStyle="simple"
              rowKey={(row, i) => row.knowledge_entity ?? `evidence-${i}`}
            />
          </div>
        </SectionCard>
      </div>

    </>
  );
}

function DrawerHeader({
  data,
  derived,
  documentTitle,
  documentType,
}: {
  data: KnowledgeQualityResult;
  derived: KQDerived;
  documentTitle?: string;
  documentType?: string;
}) {
  const docLabel = documentTitle || data.document?.title;
  const docTypeRaw = documentType || data.document?.document_type;

  return (
    <header className="sticky top-0 z-10 flex flex-col gap-3 border-b border-border bg-card p-4 pr-14 sm:p-6 sm:pr-16 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div
          className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${LAYER_ICON_BOX_TONE.knowledgeQuality.box}`}
        >
          <Star
            className={`size-5 ${LAYER_ICON_BOX_TONE.knowledgeQuality.icon}`}
          />
        </div>
        <div className="min-w-0 flex flex-col gap-1.5">
          <div className="text-lg font-bold text-foreground">
            {KQ_HEADER_COPY.title}
          </div>
          <p className="text-xs text-muted-foreground max-w-xl break-words">
            {KQ_HEADER_COPY.bodyDescription}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 pt-1">
            {docLabel && <ChipMuted>{docLabel}</ChipMuted>}
            {docTypeRaw && <DocTypeBadge type={docTypeRaw} />}
            <ValidatedPill />
          </div>
        </div>
      </div>
      <div className="flex flex-col items-end">
        <span className="text-[11px] text-muted-foreground">
          {KQ_HEADER_COPY.overallLabel}
        </span>
        <span className="text-3xl font-bold text-emerald-400 tabular-nums">
          {derived.overallPct}
        </span>
      </div>
    </header>
  );
}

function ChipMuted({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-md border border-border bg-background/60 px-2 py-0.5 text-[11px] text-foreground break-words">
      {children}
    </span>
  );
}

function DocTypeBadge({ type }: { type: string }) {
  const key = String(type).toUpperCase();
  const cls = DOC_TYPE_COLORS[key] ?? DOC_TYPE_FALLBACK_BADGE_CLS;
  return (
    <span
      className={`inline-flex items-center justify-center px-2 py-0.5 rounded-[4px] text-[10px] font-bold ${cls}`}
    >
      {formatDocType(key)}
    </span>
  );
}

function ValidatedPill() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-400">
      <span className="size-1.5 rounded-full bg-emerald-400" />
      {KQ_HEADER_COPY.validatedLabel}
    </span>
  );
}

function SummaryTiles({ derived }: { derived: KQDerived }) {
  const summary = derived.summary;
  return (
    <div className="grid grid-cols-2 gap-3 p-4 md:grid-cols-3 lg:grid-cols-5">
      <StatTile
        value={summary.knowledge_entities_detected ?? 0}
        label={KQ_SUMMARY_LABELS.entitiesDetected}
        tone="cyan"
      />
      <StatTile
        value={derived.overallPct}
        label={KQ_SUMMARY_LABELS.averageCompleteness}
        tone="emerald"
      />
      <StatTile
        value={derived.richnessPct}
        label={KQ_SUMMARY_LABELS.averageRichness}
        tone="indigo"
      />
      <StatTile
        value={derived.trustPct}
        label={KQ_SUMMARY_LABELS.averageTrust}
        tone="cyan"
      />
      <StatTile
        value={summary.knowledge_quality_findings ?? 0}
        label={KQ_SUMMARY_LABELS.findingsRaised}
        tone="amber"
      />
    </div>
  );
}

function MissingAttrsBanner({ derived }: { derived: KQDerived }) {
  const attrLabel = pluralize(derived.missingAttrs, "attribute", "attributes");
  return (
    <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-[12px] text-amber-300">
      <AlertTriangle className="mt-0.5 size-4 flex-none" />
      <div className="min-w-0 break-words">
        <div className="font-semibold text-amber-200">
          {derived.missingAttrs} {attrLabel} missing
        </div>
        <div className="mt-0.5 opacity-90">{derived.missingAttrsBannerText}</div>
      </div>
    </div>
  );
}

